import React, { useState, useEffect } from "react";
import { Card, CardContent, CardMedia, Chip, IconButton, Typography, Box } from "@material-ui/core";
import { MoreVert } from "@material-ui/icons";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { SitePhotoDto, PHOTO_CATEGORY_LABELS, PhotoCategory } from "../../../../../../../dtos/sitePhoto";

const useStyles = makeStyles((theme: Theme) => ({
    photoCard: {
        position: "relative",
        height: "240px",
        width: "100%",
        borderRadius: "12px",
        overflow: "hidden",
        transition: "all 0.3s ease",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: theme.shadows[8],
            "& .photoActions": {
                opacity: 1,
            }
        }
    },
    photoActions: {
        position: "absolute",
        top: "12px",
        right: "12px",
        opacity: 0,
        transition: "opacity 0.2s",
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderRadius: "6px",
        backdropFilter: "blur(4px)",
    },
    photoImage: {
        height: "100px",
        width: "100%",
        cursor: "pointer",
        backgroundSize: "cover",
        backgroundPosition: "center",
        objectFit: "cover",
        flexShrink: 0,
    },
    photoImagePlaceholder: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        color: "#999",
        height: "220px",
        width: "100%",
        flexShrink: 0,
    },
    photoInfo: {
        padding: "12px 16px",
        height: "100px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        flexGrow: 1,
    },
    photoTitle: {
        fontSize: "0.9rem",
        fontWeight: 500,
        lineHeight: 1.3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        wordBreak: "break-word",
        whiteSpace: "pre-line",
        marginBottom: "8px",
    },
    categoryChip: {
        height: "24px",
        fontSize: "0.75rem",
        alignSelf: "flex-start",
    },
    photoMeta: {
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
        marginTop: "4px",
        lineHeight: 1.2,
    }
}));

interface PhotoCardProps {
    photo: SitePhotoDto;
    photoUrl?: string;
    onPhotoClick: (photo: SitePhotoDto) => void;
    onMenuOpen: (event: React.MouseEvent<HTMLElement>, photo: SitePhotoDto) => void;
}

export const PhotoCard: React.FC<PhotoCardProps> = ({
    photo,
    photoUrl,
    onPhotoClick,
    onMenuOpen
}) => {
    const classes = useStyles();
    const [imageError, setImageError] = useState(false);

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString();
    };

    const handleImageError = () => {
        setImageError(true);
    };

    useEffect(() => {
        setImageError(false);
    }, [photoUrl]);

    return (
        <Card className={classes.photoCard}>
            {photoUrl && !imageError ? (
                <CardMedia
                    className={classes.photoImage}
                    image={photoUrl}
                    title={photo.description || photo.fileName}
                    onClick={() => onPhotoClick(photo)}
                    onError={handleImageError}
                />
            ) : (
                <div
                    className={`${classes.photoImage} ${classes.photoImagePlaceholder}`}
                >
                    {imageError ? "Failed to load" : "Loading..."}
                </div>
            )}

            <div className={`photoActions ${classes.photoActions}`}>
                <IconButton
                    size="small"
                    onClick={(e) => onMenuOpen(e, photo)}
                >
                    <MoreVert />
                </IconButton>
            </div>

            <CardContent className={classes.photoInfo}>
                <Typography className={classes.photoTitle} title={photo.description || photo.fileName}>
                    {photo.description || photo.fileName}
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="flex-end">
                    {photo.category && (
                        <Chip
                            label={PHOTO_CATEGORY_LABELS[photo.category as PhotoCategory]}
                            size="small"
                            className={classes.categoryChip}
                            color="primary"
                            variant="outlined"
                        />
                    )}
                    <div className={classes.photoMeta}>
                        <div>{formatDate(photo.uploadedAt)}</div>
                    </div>
                </Box>
            </CardContent>
        </Card>
    );
}; 